import React from "react";
import { MapPin, Clock, Bell } from "lucide-react";
import { apptToDate, formatDateShort, formatTimePretty, getReminders } from "../lib/dateUtils";

export const AppointmentCard = ({ appt, onClick, highlight = false }) => {
  const dt = apptToDate(appt);
  const reminders = getReminders(appt);
  const hasReminder = reminders.length > 0;

  return (
    <button
      type="button"
      onClick={() => onClick?.(appt)}
      data-testid={`appointment-card-${appt.id}`}
      className={`glass-card ${highlight ? "glass-card-active" : ""} rounded-2xl w-full text-left p-4 sm:p-5 fade-up transition-all duration-300 hover:translate-y-[-2px] focus:outline-none focus-visible:ring-1 focus-visible:ring-[#D4AF37]/60`}
    >
      <div className="flex items-start gap-4 sm:gap-5">
        {/* Date Column */}
        <div className="flex flex-col items-center justify-center min-w-[52px] sm:min-w-[60px] pr-3 sm:pr-4 border-r border-[#D4AF37]/15 shrink-0">
          <span className="eyebrow text-[10px] text-[#D4AF37]/80">
            {dt ? formatDateShort(dt).split(",")[0] : ""}
          </span>
          <span className="font-serif text-3xl text-[#F5F5F5] leading-none mt-1">
            {dt ? dt.getDate() : "—"}
          </span>
          <span className="text-[10px] uppercase tracking-[0.25em] text-[#8E8E93] mt-1">
            {dt ? dt.toLocaleString("en", { month: "short" }) : ""}
          </span>
        </div>

        {/* Body */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-serif text-xl text-[#F5F5F5] leading-snug truncate">
              {appt.title}
            </h3>
            {hasReminder && (
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] tracking-[0.18em] uppercase"
                style={{
                  background: "rgba(212, 175, 55, 0.1)",
                  color: "#D4AF37",
                  border: "1px solid rgba(212, 175, 55, 0.35)",
                }}
                data-testid={`reminder-badge-${appt.id}`}
              >
                <Bell size={10} />
                {reminders[reminders.length - 1].key === "now"
                  ? "Now"
                  : reminders[reminders.length - 1].key === "3hr"
                    ? "3h"
                    : "1d"}
              </span>
            )}
          </div>

          <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-2 text-[#8E8E93] text-sm">
            <span className="inline-flex items-center gap-1.5">
              <Clock size={13} className="text-[#D4AF37]/70" />
              <span className="font-mono-tight text-[#F3E5AB]/90">
                {dt ? formatTimePretty(dt) : appt.time}
              </span>
            </span>
            {appt.location ? (
              <span className="inline-flex items-center gap-1.5 truncate max-w-full">
                <MapPin size={13} className="text-[#D4AF37]/70" />
                <span className="truncate">{appt.location}</span>
              </span>
            ) : null}
          </div>

          {appt.note ? (
            <p className="mt-3 text-sm text-[#8E8E93] line-clamp-2 leading-relaxed">
              {appt.note}
            </p>
          ) : null}
        </div>
      </div>
    </button>
  );
};

export default AppointmentCard;
